import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import Radom, { IOrganization } from '../state/Radom'
import User from '../state/User'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

interface FeatureRow {
  label: string
  description: string
  enabled: boolean
}

function buildFeatureRows(org: IOrganization): FeatureRow[] {
  const f = org.features
  const rows: FeatureRow[] = []
  console.log(org)
  if (f == null) return rows

  rows.push({
    label: 'Open Banking',
    description: 'Accept fiat payments via open banking rails (SEPA, ACH, etc.).',
    enabled: !!f.isOpenBankingEnabled
  })
  rows.push({
    label: 'Swap',
    description: 'Automatically convert received crypto assets to a target currency.',
    enabled: !!f.isSwapEnabled
  })
  rows.push({
    label: 'Multi-leg swap',
    description: 'Cross-chain swaps through multiple liquidity legs.',
    enabled: !!f.isMultiLegSwapEnabled
  })
  rows.push({
    label: 'Auto-swap on payment',
    description: 'Trigger an automatic swap each time a payment is received.',
    enabled: !!f.autoSwapOnPaymentEnabled
  })
  rows.push({
    label: 'Conversion',
    description: 'Convert between supported assets directly from your balance.',
    enabled: !!f.isConversionEnabled
  })
  rows.push({
    label: 'Private treasury',
    description: 'Dedicated HD wallet address per organization for isolated custody.',
    enabled: !!f.isPrivateTreasuryEnabled
  })
  rows.push({
    label: 'Stablecoin payouts',
    description: 'Send batch payouts denominated in stablecoins (USDC/USDT).',
    enabled: !!f.isStablecoinPayoutsEnabled
  })
  rows.push({
    label: 'Presale',
    description: 'Run token presale campaigns with configurable pricing tiers.',
    enabled: !!f.isPresaleEnabled
  })
  rows.push({
    label: 'Monero',
    description: 'Accept XMR payments on the Monero network.',
    enabled: !!f.isMoneroEnabled
  })
  rows.push({
    label: 'USDT',
    description: 'Accept Tether (USDT) across supported networks.',
    enabled: !!f.isUsdtEnabled
  })
  rows.push({
    label: 'Tron offramp',
    description: 'Withdraw assets via Tron network.',
    enabled: !!f.isOfframpTronEnabled
  })
  rows.push({
    label: 'FBO accounts',
    description: 'For-benefit-of account structures for multi-party payment flows.',
    enabled: !!f.isFboEnabled
  })

  return rows
}

const FeaturesSettings = observer((): React.ReactElement => {
  const [loading, setLoading] = useState(true)
  const [org, setOrg] = useState<IOrganization | undefined>()

  useEffect(() => {
    if (!User._user?.defaultOrganizationId) return
    setLoading(true)
    Radom.getOrganizationById(User._user.defaultOrganizationId)
      .then(o => setOrg(o))
      .catch(err => console.error('Failed to load features', err))
      .finally(() => setLoading(false))
  }, [User._user?.defaultOrganizationId])

  const allRows = org ? buildFeatureRows(org) : []
  const rows = allRows.filter(r => r.enabled)
  const enabledCount = rows.length

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Features</SettingsTitle>
            <SettingsSubtext>
              Features enabled for your organization. Contact Radom support to request changes.
            </SettingsSubtext>
          </div>
          {!loading && (
            <StatusPill $tone={enabledCount > 0 ? 'green' : 'neutral'}>
              {enabledCount} {enabledCount === 1 ? 'feature' : 'features'} enabled
            </StatusPill>
          )}
        </SettingsHero>

        <SettingsCard>
          <SettingsCardHeader>
            <div>
              <SettingsCardTitle>Feature flags</SettingsCardTitle>
              <SettingsCardDescription>
                Features configured by Radom. For technical adjustments, please reach out to support.
              </SettingsCardDescription>
            </div>
          </SettingsCardHeader>
          <SettingsCardBody>
            {loading
              ? <Spinner />
              : <SettingsRows>
                {rows.length === 0
                  ? <SettingsRow>
                    <div>
                      <SettingsRowTitle>No features enabled</SettingsRowTitle>
                      <SettingsRowDescription>
                        Contact Radom support to enable features for your organization.
                      </SettingsRowDescription>
                    </div>
                  </SettingsRow>
                  : rows.map(row => (
                    <SettingsRow key={row.label}>
                      <div>
                        <SettingsRowTitle>{row.label}</SettingsRowTitle>
                        <SettingsRowDescription>{row.description}</SettingsRowDescription>
                      </div>
                      <StatusPill $tone='green'>Enabled</StatusPill>
                    </SettingsRow>
                  ))}
              </SettingsRows>}
          </SettingsCardBody>
        </SettingsCard>

      </SettingsPage>
    </Container>
  )
})

export default FeaturesSettings
